export type StoreAisleId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19

export interface StoreAisleMeta {
  label: string
  backgroundColor: string
  textColor: string
}

export const OTHER_AISLE_ID = 19

export const STORE_AISLES: Record<StoreAisleId, StoreAisleMeta> = {
  1: { label: 'Fruits et légumes', backgroundColor: '#58BC72', textColor: '#FFFFFF' },
  2: { label: 'Viandes, poissons et protéines', backgroundColor: '#E63946', textColor: '#FFFFFF' },
  3: { label: 'Charcuteries et prêt-à-manger', backgroundColor: '#C4391D', textColor: '#FFFFFF' },
  4: { label: 'Produits laitiers, oeufs et fromages', backgroundColor: '#8ECAE6', textColor: '#FFFFFF' },
  5: { label: 'Boulangerie et pâtisserie', backgroundColor: '#FFB703', textColor: '#FFFFFF' },
  6: { label: 'Céréales, pâtes, riz et légumineuses', backgroundColor: '#E9C46A', textColor: '#FFFFFF' },
  7: { label: 'Conserves, soupes et sauces', backgroundColor: '#8974B5', textColor: '#FFFFFF' },
  8: { label: 'Cuisson, épices et condiments', backgroundColor: '#5A875A', textColor: '#FFFFFF' },
  9: { label: 'Collations et confiseries', backgroundColor: '#F4A261', textColor: '#FFFFFF' },
  10: { label: 'Breuvages et boissons', backgroundColor: '#FF914D', textColor: '#FFFFFF' },
  11: { label: 'Produits alcoolisés', backgroundColor: '#000000', textColor: '#FFFFFF' },
  12: { label: 'Produits surgelés', backgroundColor: '#286A6C', textColor: '#FFFFFF' },
  13: { label: 'Bio, naturel et spécialisé', backgroundColor: '#7CB518', textColor: '#FFFFFF' },
  14: { label: 'Bébés et enfants', backgroundColor: '#F9C6D3', textColor: '#000000' },
  15: { label: 'Animaux', backgroundColor: '#A98467', textColor: '#FFFFFF' },
  16: { label: 'Hygiène, beauté et santé', backgroundColor: '#FF6F91', textColor: '#FFFFFF' },
  17: { label: 'Maison, ménage et papier', backgroundColor: '#8ECCB7', textColor: '#000000' },
  18: { label: 'Café, thé et boissons chaudes', backgroundColor: '#6F4E37', textColor: '#FFFFFF' },
  19: { label: 'Autre ou non applicable', backgroundColor: '#FFFFFF', textColor: '#000000' },
}

export interface SortedStoreAisle extends StoreAisleMeta {
  id: StoreAisleId
}

export function getAisleMeta(aisleId: unknown): StoreAisleMeta | null {
  const parsedAisleId = Number(aisleId)
  if (!Number.isInteger(parsedAisleId)) {
    return null
  }

  return STORE_AISLES[parsedAisleId as StoreAisleId] ?? null
}

export function parseCachedAisleId(value: unknown): number | null {
  if (value == null) {
    return null
  }
  const parsed = Number(value)
  if (!Number.isInteger(parsed) || !getAisleMeta(parsed)) {
    return null
  }
  return parsed
}

function hexToRgb(hex: string): { r: number, g: number, b: number } | null {
  const normalized = hex.replace('#', '')
  if (normalized.length !== 6)
    return null

  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  }
}

function darkenHexRgb(hex: string, factor: number): string {
  const rgb = hexToRgb(hex)
  if (!rgb)
    return hex

  const darken = (value: number) => Math.max(0, Math.round(value * (1 - factor)))
  return `#${[darken(rgb.r), darken(rgb.g), darken(rgb.b)].map(value => value.toString(16).padStart(2, '0')).join('')}`
}

function getRelativeLuminance(hex: string): number | null {
  const rgb = hexToRgb(hex)
  if (!rgb)
    return null

  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
}

/** Assombrissement personnalisé pour le texte d'étiquette (allées pâles). */
const AISLE_DISPLAY_TEXT_DARKEN: Partial<Record<StoreAisleId, number>> = {
  4: 0.26, // Produits laitiers — très légèrement moins foncé
  6: 0.26, // Céréales — très légèrement moins foncé
  14: 0.23, // Bébés — un mini peu moins foncé
}

/** Texte d'étiquette sur fond transparent : même teinte que l'allée, assombrie si pâle. */
export function getAisleDisplayTextColor(backgroundColor: string, aisleId?: unknown): string {
  const luminance = getRelativeLuminance(backgroundColor)
  if (luminance == null)
    return backgroundColor

  const parsedAisleId = aisleId != null ? Number(aisleId) : Number.NaN
  const customDarken = Number.isInteger(parsedAisleId)
    ? AISLE_DISPLAY_TEXT_DARKEN[parsedAisleId as StoreAisleId]
    : undefined

  if (customDarken != null)
    return darkenHexRgb(backgroundColor, customDarken)

  if (luminance >= 0.7)
    return darkenHexRgb(backgroundColor, 0.3)

  if (luminance >= 0.62)
    return darkenHexRgb(backgroundColor, 0.18)

  return backgroundColor
}

export function getDisplayAisleMeta(aisleId: unknown): StoreAisleMeta {
  const parsedAisleId = Number(aisleId)
  const meta = getAisleMeta(aisleId)
  if (!meta) {
    return {
      label: 'Allée inconnue',
      backgroundColor: '#E5E7EB',
      textColor: '#374151',
    }
  }

  return {
    ...meta,
    textColor: getAisleDisplayTextColor(meta.backgroundColor, parsedAisleId),
  }
}

export function getSortedAisles(): SortedStoreAisle[] {
  const aisles = Object.entries(STORE_AISLES).map(([id, aisle]) => ({
    id: Number(id) as StoreAisleId,
    ...aisle,
  }))

  const otherAisle = aisles.find(aisle => aisle.id === OTHER_AISLE_ID)
  const regularAisles = aisles
    .filter(aisle => aisle.id !== OTHER_AISLE_ID)
    .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }))

  return otherAisle ? [...regularAisles, otherAisle] : regularAisles
}

export function useStoreAisles() {
  return {
    STORE_AISLES,
    OTHER_AISLE_ID,
    getAisleMeta,
    getAisleDisplayTextColor,
    getDisplayAisleMeta,
    getSortedAisles,
    parseCachedAisleId,
  }
}
